import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { newsletterSchema, getFieldErrors, type FieldErrors } from "@/lib/validations";
import { FormError } from "@/components/ui/form-error";
import { useLanguage } from "@/contexts/LanguageContext";

interface NewsletterDialogProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function NewsletterDialog({ children, open, onOpenChange }: NewsletterDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open : internalOpen;

  const [email, setEmail] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const { toast } = useToast();
  const { t } = useLanguage();

  const validateField = (field: string, value: unknown) => {
    const data = {
      email: field === "email" ? value : email,
      gdprConsent: field === "gdprConsent" ? value : gdprConsent,
    };
    const result = newsletterSchema.safeParse(data);
    const fieldErrors = getFieldErrors(result);
    setErrors(prev => ({ ...prev, [field]: fieldErrors[field] || "" }));
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isControlled) setInternalOpen(isOpen);
    onOpenChange?.(isOpen);
    if (!isOpen) {
      setTimeout(() => { setEmail(""); setGdprConsent(false); setSubmitted(false); setErrors({}); }, 300);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = newsletterSchema.safeParse({ email, gdprConsent });
    if (!result.success) { setErrors(getFieldErrors(result)); return; }
    setErrors({});
    setIsLoading(true);
    try {
      const { error } = await supabase.from("early_access_signups").insert({
        email: result.data.email,
        user_type: "newsletter",
        gdpr_consent_at: new Date().toISOString(),
      });
      if (error) {
        if (error.code === "23505") {
          toast({ title: t("newsletter.alreadySignedUpTitle"), description: t("newsletter.alreadySignedUpDesc") });
        } else { throw error; }
      } else {
        supabase.functions.invoke("send-newsletter-confirmation", {
          body: { email: result.data.email },
        }).catch(console.error);
        setSubmitted(true);
        toast({ title: t("newsletter.successToastTitle"), description: t("newsletter.successToastDesc") });
      }
    } catch (error) {
      console.error("Newsletter signup error:", error);
      toast({ title: t("newsletter.errorTitle"), description: t("newsletter.errorDesc"), variant: "destructive" });
    } finally { setIsLoading(false); }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent className="sm:max-w-md">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl uppercase">{t("newsletter.title")}</DialogTitle>
              <DialogDescription>{t("newsletter.description")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="space-y-2">
                <Label htmlFor="newsletter-email">{t("newsletter.emailLabel")}</Label>
                <Input
                  id="newsletter-email" type="email" placeholder={t("newsletter.emailPlaceholder")} value={email}
                  onChange={(e) => { setEmail(e.target.value); if (errors.email) validateField("email", e.target.value); }}
                  onBlur={() => validateField("email", email)}
                  className={errors.email ? "border-destructive" : ""}
                />
                <FormError message={errors.email} />
              </div>
              <div className="space-y-2">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="newsletter-gdpr" checked={gdprConsent}
                    onCheckedChange={(checked) => { const value = checked === true; setGdprConsent(value); validateField("gdprConsent", value); }}
                    className={`mt-1 ${errors.gdprConsent ? "border-destructive" : ""}`}
                  />
                  <Label htmlFor="newsletter-gdpr" className="text-sm font-normal text-muted-foreground cursor-pointer leading-relaxed">
                    {t("newsletter.gdprText")}
                  </Label>
                </div>
                <FormError message={errors.gdprConsent} />
              </div>
              <Button type="submit" className="w-full uppercase" size="lg" disabled={isLoading}>
                {isLoading ? t("newsletter.signingUp") : t("newsletter.signUp")}
              </Button>
              <p className="text-xs text-muted-foreground text-center">{t("newsletter.privacyNote")}</p>
            </form>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-primary" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-2xl uppercase">{t("newsletter.successTitle")}</DialogTitle>
              <DialogDescription className="text-base mt-2">{t("newsletter.successDesc")}</DialogDescription>
            </DialogHeader>
            <Button onClick={() => handleOpenChange(false)} variant="outline" className="mt-6 uppercase">
              {t("newsletter.close")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
